/**
 * THE SIDEBAR, AND THE ONE PLACE A SURFACE OR AN ACCESS LEVEL GETS A
 * HUMAN NAME.
 *
 * This used to live inside `components/AppShell.vue`, where only the
 * sidebar could see it. The Role Mapping page then had no way to say
 * "Backend Console" and printed the raw surface slug `backend_console`
 * instead — an operator deciding who may reach a page was reading a
 * different vocabulary from the menu they were deciding about.
 *
 * The alternative was a surface -> label map on the server, beside
 * `PAGE_PATHS`. That was rejected: the label would then exist in three
 * places (here, `router.ts`'s `meta.title`, and the server) and this
 * repository has already paid for a constant kept in four — including a
 * recorded prompt telling callers "four for charger" while the service
 * filed throttle. The menu's own definition is the honest source for
 * what the menu calls things, so both pages read it.
 *
 * WHAT IS *NOT* HERE: who may reach any of it. That is decided once, on
 * the server, by `backend/app/services/surface_policy.py`, and arrives
 * on the auth payload as a flat list of surfaces. There is deliberately
 * no TypeScript copy of the role mapping to drift — see the note in
 * `views/RoleMappingView.vue`.
 *
 * The `surface` strings here must match `meta.surface` on the matching
 * route in `router.ts`. `backend/tests/test_role_matrix_r7.py` parses
 * this file and fails if one of them is not a surface the server knows.
 */

export interface NavItem {
  label: string;
  to: string;
  icon: string;
  /** The surface_policy surface this page belongs to; omitted = always shown. */
  surface?: string;
  exact?: boolean;
}

// Inline SVG icons taken from the design reference.
const icons = {
  dashboard:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>',
  usage:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 17l5-6 4 3 6-8"/><path d="M3 21h18"/></svg>',
  tracing:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12h4l2.5-7 4 14 2.5-7h7"/></svg>',
  // Horizontal bars — the shape of the gate-wait chart the page leads on.
  analytics:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v16h16"/><path d="M7 8h10M7 12h6M7 16h12"/></svg>',
  approvals:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 11.5V7a1.5 1.5 0 0 1 3 0v3.5"/><path d="M10 10V4.5a1.5 1.5 0 0 1 3 0V10"/><path d="M13 10.2V6.5a1.5 1.5 0 0 1 3 0v6"/><path d="M16 12.5V10a1.5 1.5 0 0 1 3 0v5a7 7 0 0 1-7 7h-.8a7 7 0 0 1-5.5-2.7L3 15.4a1.7 1.7 0 0 1 2.6-2.1L7 15"/></svg>',
  organizations:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 21h20"/><path d="M4 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16"/><path d="M14 10h4a2 2 0 0 1 2 2v9"/><path d="M7 7h1m2 0h1M7 11h1m2 0h1M7 15h1m2 0h1m6 0h1m-1 4h1"/></svg>',
  departments:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18M5 21V7l7-4 7 4v14"/><path d="M9 9h1m4 0h1M9 13h1m4 0h1M9 17h1m4 0h1"/></svg>',
  users:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.8-3.2 3.4-5 6.5-5s5.7 1.8 6.5 5"/><circle cx="17.5" cy="9.5" r="2.5"/><path d="M16 15.2c2.6.3 4.6 1.8 5.5 4.8"/></svg>',
  providers:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3 5.5L21 9l-4 4.5 1 6.5-6-3-6 3 1-6.5L3 9l6-1.5z"/></svg>',
  agents:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="7" width="14" height="12" rx="3"/><circle cx="9.5" cy="12.5" r="1"/><circle cx="14.5" cy="12.5" r="1"/><path d="M12 7V4m-6 9H3m18 0h-3M9 16.5h6"/></svg>',
  knowledge:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4a2 2 0 0 0-2-2H6.5A2.5 2.5 0 0 0 4 4.5z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>',
  tools:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4L15 12l-3-3z"/></svg>',
  workflows:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="6" height="6" rx="1.5"/><rect x="16" y="2" width="6" height="6" rx="1.5"/><rect x="16" y="14" width="6" height="6" rx="1.5"/><path d="M8 7h4m0 0V5h4m-4 2v10h4m-4-6h4"/></svg>',
  forms:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M14 2v4h4M9 12h6M9 16h4"/></svg>',
  // A document with a spark — a paper form being read by something.
  askai:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-6"/><path d="M8 8h5M8 12h4M8 16h6"/><path d="M18.5 2l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1z"/></svg>',
  scheduler:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/></svg>',
  // A terminal window — the shape of "look at the process", not "look
  // at the data".
  console:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2.5" y="4" width="19" height="16" rx="2.5"/><path d="M2.5 8.5h19"/><path d="M6.5 12.5l2.5 2-2.5 2"/><path d="M12 16.5h5"/></svg>',
  // A grid with a tick — the shape of "which role, which page", which is
  // literally what the page renders.
  rolemapping:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2.5"/><path d="M3 9h18M9 9v12"/><path d="M12.5 13.5l1.8 1.8 3.7-3.8"/></svg>',
  apikeys:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="8" cy="15" r="4.5"/><path d="M11.5 11.5L21 2m-4 2l3 3m-6 0l2.5 2.5"/></svg>',
  channels:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.5 12.5a6 6 0 0 1-6 6H3l1.9-2.4a6 6 0 1 1 9.6-3.6z"/><path d="M17.5 8.5a7.5 7.5 0 0 1 2.2 4.3"/><path d="M19.6 5.4A11 11 0 0 1 22.8 12"/></svg>',
  // TWO LINKED HALVES — a connector, not a spanner. `icons.tools` would
  // have been the lazy choice and it sits four rows above Connections in
  // the same section; the note on `chatflow` below says why two
  // identical glyphs beside two similarly-named entries is the one thing
  // a sidebar must not do. A tool is one call this platform MAKES; a
  // connection is a system it KNOWS HOW TO REACH, and the shapes should
  // not imply they are the same kind of thing.
  connections:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9.5 14.5l-2.6 2.6a3.7 3.7 0 0 1-5.2-5.2l2.6-2.6"/><path d="M14.5 9.5l2.6-2.6a3.7 3.7 0 0 1 5.2 5.2l-2.6 2.6"/><path d="M9 15l6-6"/></svg>',
  // A FLASK. Not the workflows glyph and not the chat-flow one: this is
  // a page for PRACTISING on throwaway graphs, and giving it either of
  // their icons would say it is another place to do the real thing.
  lab:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 2v6.5L3.8 17a2.5 2.5 0 0 0 2.1 3.8h12.2a2.5 2.5 0 0 0 2.1-3.8L15 8.5V2"/><path d="M7.5 2h9M7.2 14h9.6"/></svg>',
  chat:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a8 8 0 0 1-8 8H4l2.3-2.9A8 8 0 1 1 21 12z"/></svg>',
  // The same speech bubble with a BRANCH inside it — a conversation that
  // is a graph. It sits directly above Chat Console, and the note on
  // `telephony` two entries up applies here for the same reason: two
  // identical glyphs beside two similarly-named entries is the one thing
  // a sidebar must not do.
  chatflow:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a7.5 7.5 0 0 1-7.5 7.5H4.5l2.2-2.7A7.5 7.5 0 1 1 21 11.5z"/><path d="M9 8v6h3m0-3h3m0 0V9m0 2v2"/></svg>',
  // A speech bubble with a handset inside it — the Channel Analytics
  // page, which is one page about calls AND conversations. Neither the
  // bare handset (that is Voice Test Call, three entries down) nor the
  // bare bubble (Chat Console) would have said "both".
  channelanalytics:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a7.5 7.5 0 0 1-7.5 7.5H4.5l2.2-2.7A7.5 7.5 0 1 1 21 11.5z"/><path d="M9.2 7.8h1.9l.9 2.4-1.2.9a7 7 0 0 0 3.4 3.4l.9-1.2 2.4.9v1.9"/></svg>',
  // A handset. The page is a test CALL, so the icon is a telephone even
  // though no telephone is involved — that is the point of the phase.
  telephony:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 2.5 5.7 2 2 0 0 1 4.5 3.5z"/></svg>',
};

/*
 * Every item names its SURFACE. Who may reach it is decided once, on the
 * server, by `backend/app/services/surface_policy.py`, and arrives on the
 * auth payload — so this file no longer carries a second, hand-kept copy
 * of the role mapping. The surfaces here must match `meta.surface` on the
 * matching route in `src/router.ts`.
 */
export const sections: { label: string; items: NavItem[] }[] = [
  // ── THE ONE PAGE EVERY ROLE REACHES ───────────────────────────────
  //
  // Chat Console is on the `chat` surface, which is EVERYONE — all six
  // roles, from super_admin to member. It used to sit last in AI Studio,
  // which put the platform's most universally available page at the
  // bottom of its most restricted section, below eleven entries a member
  // never sees. For a member the whole section rendered as two items,
  // "Workflows" and "Chat Console", under a heading about building AI.
  //
  // An empty label means no heading is drawn (see the template), so this
  // reads as a primary destination directly under the brand rather than
  // as the first member of a category.
  {
    label: "",
    items: [
      { label: "Chat Console", to: "/chat", icon: icons.chat, surface: "chat" },
    ],
  },
  {
    label: "Overview",
    items: [
      { label: "Dashboard", to: "/dashboard", icon: icons.dashboard, exact: true, surface: "dashboard" },
      { label: "Usage & Costs", to: "/usage", icon: icons.usage, surface: "usage" },
      { label: "Tracing", to: "/traces", icon: icons.tracing, surface: "traces" },
      {
        label: "Workflow Analytics",
        to: "/workflow-analytics",
        icon: icons.analytics,
        surface: "workflow_analytics",
      },
      {
        // "Voice Analytics" until CA3 gave the page a Chat tab. The
        // label follows what the page IS; the `to` follows what every
        // link already written points at, which is why one moved and the
        // other did not — see the note on the route in src/router.ts.
        label: "Channel Analytics",
        to: "/voice-analytics",
        // A speech bubble with a handset in it: the page is now BOTH
        // channels, and the bare handset would have said one of them.
        // Still not the bar chart — this sits directly under Workflow
        // Analytics and two identical glyphs beside two similarly-named
        // entries is the one thing a sidebar must not do.
        icon: icons.channelanalytics,
        surface: "voice_analytics",
      },
      { label: "Tasks", to: "/approvals", icon: icons.approvals, surface: "approvals" },
    ],
  },
  {
    label: "Organization",
    items: [
      // The outer tenancy boundary — and, since it is also where a
      // tenant's provider keys are re-entered (AI Configuration), the one
      // whose narrowing to super_admin has an operational consequence.
      // See docs/SECRET_KEY_ROTATION.md §5.
      {
        label: "Organizations",
        to: "/organizations",
        icon: icons.organizations,
        surface: "organizations",
      },
      {
        label: "Departments",
        to: "/departments",
        icon: icons.departments,
        surface: "departments",
      },
      { label: "Users", to: "/users", icon: icons.users, surface: "users" },
      {
        label: "Platform Catalogue",
        to: "/providers",
        icon: icons.providers,
        surface: "providers",
      },
      // The editor for the table that decides this very menu. It is
      // filtered by `auth.canReach` like every other entry, but its
      // surface is PINNED to super_admin on the server, so it is the one
      // item nobody can accidentally hide from themselves.
      {
        label: "Role Mapping",
        to: "/role-mapping",
        icon: icons.rolemapping,
        surface: "role_mapping",
      },
    ],
  },
  {
    label: "AI Studio",
    items: [
      { label: "AI Agents", to: "/agents", icon: icons.agents, surface: "agents" },
      // RUN-ONLY below super_admin: the page offers the list and the run
      // dialog, and the builder is not rendered. The server refuses every
      // mutation independently — see WorkflowsView's `canAuthor`.
      { label: "Workflows", to: "/workflows", icon: icons.workflows, surface: "workflows_run" },
      { label: "Forms", to: "/forms", icon: icons.forms, surface: "forms" },
      // THE CASES, WHICH IS NOT THE BUILDER. ``forms`` is the surface
      // for DESIGNING a document — super_admin, platform_developer,
      // org_admin. Searching cases is the support desk's job, and a
      // dept_admin there should not need the form editor to do it. So
      // this has a surface of its own that can be granted on its own.
      {
        label: "Form Instances",
        to: "/form-instances",
        icon: icons.forms,
        surface: "form_instances",
      },
      { label: "Ask AI", to: "/ask-ai", icon: icons.askai, surface: "ask_ai" },
      {
        label: "Knowledgebase",
        to: "/knowledge",
        icon: icons.knowledge,
        surface: "knowledge",
      },
      {
        label: "Workflow Lab",
        to: "/workflow-lab",
        icon: icons.lab,
        surface: "workflow_lab",
      },
      {
        label: "Connections",
        to: "/connections",
        icon: icons.connections,
        surface: "service_connections",
      },
      {
        label: "Tools",
        to: "/tools",
        icon: icons.tools,
        surface: "tools",
      },
      {
        label: "Scheduler",
        to: "/scheduler",
        icon: icons.scheduler,
        surface: "scheduler",
      },
      { label: "API Keys", to: "/api-keys", icon: icons.apikeys, surface: "api_keys" },
      {
        label: "Channels",
        to: "/channels",
        icon: icons.channels,
        surface: "channels",
      },
      {
        label: "Voice Test Call",
        to: "/telephony/test-call",
        icon: icons.telephony,
        surface: "telephony",
      },
      // W3 — NO MENU ENTRY FOR /voice-prompts, deliberately. It sits on
      // `workflows_edit`, which this platform keeps as a permission
      // WITHOUT a menu item: it is what lets you edit a graph, exercised
      // from inside Workflows rather than from the nav. Adding one put
      // the surface in both the menu and the map that names what the
      // menu cannot show, and `test_role_matrix_r7` refuses that —
      // correctly, because the second list exists only to describe the
      // first's gaps.
      //
      // The page is reached from the Workflows toolbar and from the
      // audio_key field in the step drawer, which is where somebody is
      // standing when they find a prompt is wrong.
      // The handset's twin: a speech bubble, because the page is a test
      // CONVERSATION even though no WhatsApp is involved — the same joke
      // the icon above makes about a telephone. The bubble has a branch
      // in it, so it is not the Chat Console's glyph twice.
      {
        label: "Chat Flow Test",
        to: "/chat-flows/test",
        icon: icons.chatflow,
        surface: "chat_flow_test",
      },
      // MOVED HERE FROM "Organization". Last in the section because it is
      // the one entry that is not a thing you BUILD — it reports on the
      // worker process the rest of this section runs on, so it reads as
      // the tail of the toolchain rather than another authoring page.
      //
      // Nothing about REACH changed: the surface is still
      // "backend_console", still BOTH_PLATFORM_AND_SUPER on the server,
      // still refused to every tenant role by the same middleware. A
      // section is a heading, not a permission — `visibleSections`
      // filters items by `canReach` and then drops empty sections, so
      // moving an item between headings cannot open or close anything.
      // The role matrix suite is unaffected for that reason.
      {
        label: "Backend Console",
        to: "/backend-console",
        icon: icons.console,
        surface: "backend_console",
      },
    ],
  },
];


/**
 * Surfaces the SERVER knows that this menu deliberately has no row for.
 *
 * The Role Mapping page lists every surface in `SURFACE_ROLES` — 33 of
 * them — while the sidebar has 24 entries. The nine below are the
 * difference, and each is a real thing an operator can re-map, so each
 * needs a name that is not its slug. Leaving them to fall back to the
 * slug would have meant the page was half-translated, which is worse
 * than not translated: the reader cannot tell whether a bare slug means
 * "no menu item" or "somebody forgot".
 *
 * The parenthetical says WHY there is no menu row, because that is the
 * question the label immediately raises.
 */
export const NON_MENU_SURFACES: Record<string, string> = {
  public: "Public endpoints (sign-in, health, webhooks)",
  session: "Session plumbing (who am I, org switcher, notifications)",
  unmapped: "Anything not mapped (the fail-closed default)",
  forms_fill: "Filling in a form instance (opened from a task or a run)",
  trace_detail: "One run's trace (opened from Tasks)",
  workflows_edit: "Workflows — authoring (the builder on the Workflows page)",
  classify: "Classification (called by workflows; no page)",
  elfa_workflow:
    "Filing a case in elfa-workflow (called by workflows; no page)",
  db_connections: "Database connections (API only)",
  shares: "Resource sharing (API only)",
};

/** surface -> { label, section, path } for every surface the menu shows. */
export const SURFACE_MENU: Record<
  string,
  { label: string; section: string; path: string }
> = Object.fromEntries(
  sections.flatMap((s) =>
    s.items
      .filter((i) => i.surface)
      .map((i) => [i.surface as string, {
        label: i.label,
        section: s.label,
        path: i.to,
      }])
  )
);

/**
 * What to call a surface on screen.
 *
 * Falls back to the slug, on purpose. A surface added to the server but
 * not yet to this file then shows as `some_new_surface` rather than
 * vanishing or rendering blank — visibly unfinished, which is the
 * failure shape you want. See `surfaceIsNamed`.
 */
export function surfaceLabel(surface: string): string {
  return SURFACE_MENU[surface]?.label ?? NON_MENU_SURFACES[surface] ?? surface;
}

/** The menu heading a surface sits under, or "" if it has no menu row. */
export function surfaceSection(surface: string): string {
  return SURFACE_MENU[surface]?.section ?? "";
}

/** False when `surfaceLabel` would have to fall back to the raw slug. */
export function surfaceIsNamed(surface: string): boolean {
  return surface in SURFACE_MENU || surface in NON_MENU_SURFACES;
}


/**
 * Access level -> what to call it on screen.
 *
 * THERE WERE FOUR HAND-KEPT COPIES OF THIS. `stores/auth.ts`,
 * `components/OrgSwitcher.vue`, `views/UsersView.vue` and
 * `views/RoleMappingView.vue` each carried their own, and R7 added
 * `platform_admin` and `platform_developer` to three of them. The fourth
 * — the Role Mapping page, the one whose entire subject is these six
 * names — was missed, so its two new columns were headed
 * `platform_admin` and `platform_developer` in raw slug while every
 * other page in the product said "Platform Admin".
 *
 * That is the failure mode this repository keeps paying for: a constant
 * stated more than once diverges at the first edit, and the copy that
 * rots is never the one you are looking at. One map, four readers.
 *
 * The order is the ladder as the Role Mapping page prints it.
 */
export const ACCESS_LEVEL_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  platform_admin: "Platform Admin",
  platform_developer: "Platform Developer",
  org_admin: "Org Admin",
  dept_admin: "Dept Admin",
  member: "Member",
};

/**
 * Falls back to the slug for the same reason `surfaceLabel` does: a role
 * the server grows before this file knows about it should look
 * unfinished, not blank.
 */
export function accessLevelLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return ACCESS_LEVEL_LABELS[role] ?? role;
}
